import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Container,
    Paper
} from '@mui/material';

const VerifyMagicLink = () => {
    const [searchParams] = useSearchParams();
    const { verifyMagicLink } = useAuth();
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [verifying, setVerifying] = useState(true);
    const verificationAttempted = useRef(false);

    useEffect(() => {
        const verifyToken = async () => {
            const token = searchParams.get('token');
            
            // Prevent double verification
            if (verificationAttempted.current) {
                return;
            }
            verificationAttempted.current = true;
            
            if (!token) {
                setError('No verification token found');
                setVerifying(false);
                return;
            }

            try {
                const result = await verifyMagicLink(token);
                if (!result.success) {
                    setError(result.message || 'Verification failed');
                    setVerifying(false);
                }
                // Don't set verifying to false on success as the auth context will handle navigation
            } catch (error) {
                setError('An error occurred during verification');
                setVerifying(false);
            }
        };

        verifyToken();
    }, [searchParams, verifyMagicLink, navigate]);

    if (verifying) {
        return (
            <Container maxWidth="sm">
                <Box 
                    sx={{ 
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 3
                    }}
                >
                    <CircularProgress />
                    <Typography variant="h6" color="textSecondary">
                        Verifying your login link...
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="sm">
                <Box 
                    sx={{ 
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 3
                    }}
                >
                    <Paper 
                        elevation={3}
                        sx={{ 
                            p: 4, 
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <Alert severity="error" sx={{ width: '100%' }}>
                            {error}
                        </Alert>
                        <Typography variant="body1" align="center">
                            Please try logging in again or contact support if the problem persists.
                        </Typography>
                        <Typography 
                            variant="body2" 
                            color="primary" 
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate('/login')}
                        >
                            Return to Login
                        </Typography>
                    </Paper>
                </Box>
            </Container>
        );
    }

    return null;
};

export default VerifyMagicLink; 